import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, TrendingUp, Users } from "lucide-react";

function Sparkline({ points, width = 220, height = 44 }) {
  if (!points || points.length === 0) return null;
  const scores = points.map(p => p.score);
  const min = Math.max(0, Math.min(...scores) - 5);
  const max = Math.min(100, Math.max(...scores) + 5);
  const range = Math.max(1, max - min);
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;
  const path = points.map((p, i) => {
    const x = i * stepX;
    const y = height - ((p.score - min) / range) * height;
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const last = points[points.length - 1];
  const lastX = (points.length - 1) * stepX;
  const lastY = height - ((last.score - min) / range) * height;
  const area = `${path} L${lastX.toFixed(1)},${height} L0,${height} Z`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         data-testid="benchmark-sparkline">
      <path d={area} fill="#0A8B8520" stroke="none"/>
      <path d={path} fill="none" stroke="#0A8B85" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={lastX} cy={lastY} r="3.5" fill="#0A8B85"/>
    </svg>
  );
}

function DistributionBar({ distribution, myScore }) {
  if (!distribution || distribution.length === 0) return null;
  const max = Math.max(...distribution.map(d => d.count), 1);
  const myBucket = Math.min(4, Math.floor((myScore || 0) / 20));
  return (
    <div className="flex items-end gap-1.5 h-16" data-testid="benchmark-distribution">
      {distribution.map((d, i) => {
        const isMe = i === myBucket;
        return (
          <div key={d.range} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex w-full flex-1 items-end">
              <div
                className={`w-full rounded-t ${isMe ? "bg-[#0A8B85]" : "bg-neutral-200"}`}
                style={{ height: `${(d.count / max) * 100}%`, minHeight: 2 }}
                title={`${d.count} businesses`}
              />
            </div>
            <div className={`text-[9px] font-bold ${isMe ? "text-[#0A8B85]" : "text-neutral-400"}`}>
              {d.range}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function BenchmarkPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/companies/me/benchmark");
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setData({ error: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3"
           data-testid="benchmark-loading" aria-busy="true">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="space-y-2">
            <div className="h-3 w-32 animate-pulse rounded bg-neutral-100"/>
            <div className="h-5 w-48 animate-pulse rounded bg-neutral-100"/>
          </div>
          <div className="h-8 w-20 animate-pulse rounded bg-neutral-100"/>
        </div>
        <div className="mt-4 h-2 animate-pulse rounded-full bg-neutral-100"/>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div className="h-12 animate-pulse rounded-lg bg-neutral-100"/>
          <div className="h-12 animate-pulse rounded-lg bg-neutral-100"/>
          <div className="h-12 animate-pulse rounded-lg bg-neutral-100"/>
        </div>
        <div
          className="mt-4 h-2 w-full overflow-hidden rounded-full bg-neutral-100"
          role="progressbar"
          aria-label="Loading cohort benchmark"
          data-testid="benchmark-loading-bar"
        >
          <div
            className="h-2 w-1/3 rounded-full bg-gradient-to-r from-neutral-200 via-[#0A8B85]/70 to-neutral-200"
            style={{ animation: "scoreShimmer 1.4s ease-in-out infinite" }}
          />
        </div>
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-neutral-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin"/> Loading cohort benchmark…
        </p>
      </div>
    );
  }
  if (!data || data.error || (data.cohort?.n || 0) === 0) {
    return null; // hide silently if cohort is empty
  }

  const my = data.score || 0;
  const c = data.cohort || {};
  const delta = my - (c.median || 0);
  const history = data.history || [];

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 lg:col-span-3"
         data-testid="benchmark-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#0A8B85]">
            Cohort benchmark
          </p>
          <h2 className="mt-1 text-lg font-bold text-neutral-900">
            How you compare
          </h2>
          <p className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
            <Users className="h-3 w-3"/> {c.n} peer{c.n === 1 ? "" : "s"} in{" "}
            <span className="font-semibold text-neutral-700">{c.label}</span>
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            Your percentile
          </div>
          <div className="text-3xl font-extrabold text-[#0A8B85]" data-testid="benchmark-percentile">
            {c.percentile != null ? `${c.percentile}%` : "—"}
          </div>
          <div className="text-[10px] text-neutral-500">of peers below you</div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* My score history sparkline */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4"
             data-testid="benchmark-history">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Your score over time
            </p>
            <TrendingUp className="h-3 w-3 text-[#0A8B85]"/>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-[#0A8B85]">{my}</span>
            <span className="text-xs text-neutral-500">/ 100</span>
          </div>
          {history.length >= 2 ? (
            <div className="mt-2">
              <Sparkline points={history} width={220} height={44}/>
              <p className="mt-1 text-[10px] text-neutral-500">
                {history.length} score{history.length === 1 ? "" : "s"} ·
                first {history[0].score} → now {history[history.length - 1].score}
              </p>
            </div>
          ) : (
            <p className="mt-3 text-[11px] text-neutral-400">
              Run your analysis again to start building a trendline.
            </p>
          )}
        </div>

        {/* Median / top quartile */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4"
             data-testid="benchmark-medians">
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            Cohort baseline
          </p>
          <div className="mt-2 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">Median peer</span>
              <span className="font-bold text-neutral-900">{c.median ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-neutral-600">Top quartile</span>
              <span className="font-bold text-neutral-900">{c.top_quartile ?? "—"}</span>
            </div>
            <div className="flex items-center justify-between border-t border-neutral-200 pt-2">
              <span className="text-neutral-600">You vs. median</span>
              <span className={`font-extrabold ${delta >= 0 ? "text-[#0A8B85]" : "text-amber-700"}`}>
                {delta >= 0 ? "+" : ""}{delta}
              </span>
            </div>
          </div>
        </div>

        {/* Distribution */}
        <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
              Where peers cluster
            </p>
            <span className="text-[9px] text-neutral-400">your bucket highlighted</span>
          </div>
          <div className="mt-3">
            <DistributionBar distribution={c.distribution} myScore={my}/>
          </div>
        </div>
      </div>
    </div>
  );
}
